import React, { useEffect } from "react";
import styled, { useTheme } from "styled-components/native";
import { CheckBox, Spacer, VDBText } from "@components/atoms";
import { CollectionProps, menuOptionSelected } from "./types";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { Platform } from "react-native";
import { Menu, MenuItem, View } from "@gluestack-ui/themed";
import { useState } from "react";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ShareModal from "../Share";
import { ShareType } from "../Share/types";
import { IconState } from "../IconUtils";
import useThreeDotMenuState from "../../../hooks/useThreeDotMenuState";
import { VDBIcons } from "@utils/VDBIcons";
import useFavoriteState from "../../../hooks/useFavoriteState";
import { ProductCategory } from "@utils/constants";
import { getBoxShadow } from "@utils/product/productValidators";
import { ShareVia } from "../Share/utils/utils";
import { DefaultTheme } from "styled-components/native";

const CollectionView = ({
  testID,
  collection,
  isFavorites,
  count,
  stock_item_ids,
  threeDotOptions,
  menuOptions,
  shareOptions,
  fromFavoriteScreen,
  showCheckbox,
  checkboxFunction,
  selectCheckbox,
  showSelected,
}: CollectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = theme.isTablet || theme.isLargeScreen;
  const [checkboxSelected, setCheckboxSelected] = useState(false);
  const typography = useTypographyStore();
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set([]));
  const [showViewSelected, setShowViewSelected] = useState(false);
  const [showShareModal, setshowShareModal] = useState(false);

  const {
    setThreeDotMenuOpen,
    setThreeDotIconState,
    handlePressIn,
    handlePressOut,
    handlePress,
    getThreeDotIconBackgroundColor,
  } = useThreeDotMenuState(showSelected);

  const {
    setFavoriteIconState,
    setFavoriteMenuOpen,
    handleFavoritesPressIn,
    handleFavoritesPressOut,
    handleFavoritesPress,
    getFavoriteIconColor,
  } = useFavoriteState(true);

  const [selectedOption, setSelectedOption] = useState("");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  useEffect(() => {
    setShowViewSelected(showSelected);
  }, [showSelected]);

  const dateFormat = "D MMMM YYYY";
  const todaysDate = moment().format(dateFormat);

  const getDate = (updated_at: string) => {
    var currentDate = moment(updated_at).format(dateFormat);
    return currentDate === todaysDate ? t("global.text.today") : currentDate;
  };

  const checkStateChanged = (checked: boolean) => {
    setCheckboxSelected(checked);
    checkboxFunction(checked, collection?.id, isFavorites);
  };

  const handleSelectionChange = (keys: Set<string>) => {
    const keysSet = new Set(Array.from(keys).map((key) => String(key)));
    if (keysSet.entries.length > 0) {
      setSelectedKeys(keysSet);
    }
    const [firstKey] = keysSet;
    if (firstKey) {
      setTimeout(() => {
        menuOptionSelected(
          firstKey,
          setshowShareModal,
          setSelectedOption,
          count,
          collection,
          isFavorites,
          t
        );
      }, 100);
    }
  };
  return (
    <TopLevelContainer
      id="favorite"
      isFavorites={isFavorites}
      checkboxSelected={checkboxSelected}
      fromFavoriteScreen={fromFavoriteScreen}
      showViewSelected={showViewSelected}
      isTablet={isTablet}
      isLargeScreen={theme.isLargeScreen}
    >
      <ParentContainer
        showBorderRadius={isFavorites || !fromFavoriteScreen}
        showSelected={showViewSelected}
        isLargeScreen={theme.isLargeScreen}
        isFavorites={isFavorites}
        fromFavoriteScreen={fromFavoriteScreen}
      >
        <IconAndCollectionContainer>
          <IconContainer>
            <ImageContainer isEmpty={count && count > 0}>
              <VDBIcon
                name={
                  isFavorites
                    ? count && count > 0
                      ? VDBIcons.FAV
                      : VDBIcons.FAV_EMPTY
                    : VDBIcons.COLLECTION
                }
                size={24}
                variant={IconsVariant.VDBCustom}
                color={
                  showViewSelected
                    ? "core-03"
                    : count && count > 0
                    ? isFavorites
                      ? theme["error-01"]
                      : theme["core-06"]
                    : theme["core-04"]
                }
              />
            </ImageContainer>
          </IconContainer>
          <CollectionInfoContainer>
            <Spacer size={SpaceType.Tiny} />
            <VDBText
              color={
                theme[
                  showViewSelected
                    ? theme.isLargeScreen
                      ? "core-05"
                      : "option-cont-active"
                    : "core-05"
                ]
              }
              type={typography["Body-M"]}
            >
              {isFavorites ? t("favorites.favorites.text") : collection?.name}
            </VDBText>
            <CountContainer>
              <VDBText
                color={
                  theme[
                    showViewSelected
                      ? theme.isLargeScreen
                        ? "core-05"
                        : "option-cont-active"
                      : "core-05"
                  ]
                }
                type={typography["Minor-R"]}
              >
                {(isFavorites ? count : collection?.count) +
                  " " +
                  t("favorites.items.text")}
              </VDBText>
              <Spacer size={SpaceType.Basic} />
              {false && (
                <>
                  <VDBText
                    color={
                      theme[
                        showViewSelected
                          ? theme.isLargeScreen
                            ? "core-06"
                            : "option-cont-active"
                          : "core-06"
                      ]
                    }
                    type={typography["Minor-R"]}
                  >
                    {t("favorites.defaultCollection.text")}
                  </VDBText>
                  <Spacer size={SpaceType.Tiny} />
                  <VDBIcon
                    color={theme["success-01"]}
                    size={16}
                    variant={IconsVariant.VDBCustom}
                    name={VDBIcons.CHECKMARK}
                  />
                </>
              )}
            </CountContainer>
          </CollectionInfoContainer>
        </IconAndCollectionContainer>

        {showCheckbox && (
          <CheckboxContainer>
            <CheckBox
              testID={testID ? `${testID}-checkbox` : undefined}
              fillColor={theme["knob-bor"]}
              checkBoxSize={20}
              checkedFunction={checkStateChanged}
              isChecked={selectCheckbox ?? false}
            />
          </CheckboxContainer>
        )}

        {!showCheckbox && (
          <OptionsContainer
            isLargeScreen={theme.isLargeScreen || theme.isTablet}
          >
            <OptionsWrapper
              menuOptionsSize={
                menuOptions && menuOptions.length > 0 ? menuOptions.length : 0
              }
            >
              {shareOptions && shareOptions.length > 0 && (
                <StyledMenu
                  mt={Platform.OS === "ios" ? 0 : -30}
                  py={0}
                  borderRadius={8}
                  marginRight={isTablet ? 0 : 10}
                  placement={isTablet ? "bottom right" : "bottom left"}
                  onOpen={() => {
                    setFavoriteMenuOpen(true);
                    setFavoriteIconState(IconState.Selected);
                  }}
                  onClose={() => {
                    setFavoriteMenuOpen(false);
                    setFavoriteIconState(IconState.Unselected);
                  }}
                  selectionMode="single"
                  backgroundColor={theme["core-01"]}
                  selectedKeys={selectedKeys}
                  trigger={({ ...triggerProps }) => {
                    return (
                      <PressableContainer
                        onPressIn={
                          collection?.count !== 0
                            ? handleFavoritesPressIn
                            : undefined
                        }
                        onPressOut={
                          collection?.count !== 0
                            ? handleFavoritesPressOut
                            : undefined
                        }
                        onPress={
                          collection?.count !== 0
                            ? handleFavoritesPress
                            : undefined
                        }
                        onHoverIn={() => setHoveredIndex(0)}
                        onHoverOut={() => setHoveredIndex(null)}
                        {...(collection?.count !== 0 && triggerProps)}
                      >
                        <VDBIcon
                          color={
                            hoveredIndex === 0
                              ? theme["core-05"]
                              : getFavoriteIconColor(theme)
                          }
                          size={24}
                          variant={IconsVariant.VDBCustom}
                          name={VDBIcons.SHARE_ALT}
                        />
                      </PressableContainer>
                    );
                  }}
                >
                  {shareOptions &&
                    shareOptions.map((option, index) => (
                      <MenuItem
                        key={option.name}
                        textValue={option.label}
                        style={{
                          padding: 0,
                          backgroundColor: theme.transparent,
                        }}
                        onPress={() => {
                          const keysSet = new Set([option.name as string]);
                          handleSelectionChange(keysSet);
                        }}
                      >
                        <MenuItemContainer>
                          <MenuItemStyle
                            optionSize={shareOptions.length}
                            index={index}
                          >
                            <VDBText
                              color={theme["core-05"]}
                              type={typography["Minor-M"]}
                            >
                              {t(option.label)}
                            </VDBText>
                          </MenuItemStyle>
                          {index < shareOptions.length - 1 && (
                            <LineView theme={theme} />
                          )}
                        </MenuItemContainer>
                      </MenuItem>
                    ))}
                </StyledMenu>
              )}
              <Spacer size={SpaceType.Tiny} />
              {menuOptions && menuOptions.length > 0 && (
                <StyledMenu
                  mt={Platform.OS === "ios" ? 0 : -30}
                  py={0}
                  marginRight={isTablet ? 0 : 10}
                  placement={isTablet ? "bottom right" : "bottom left"}
                  onOpen={() => {
                    setThreeDotMenuOpen(true);
                    setThreeDotIconState(IconState.Selected);
                  }}
                  onClose={() => {
                    setThreeDotMenuOpen(false);
                    setThreeDotIconState(IconState.Unselected);
                  }}
                  backgroundColor={theme["core-01"]}
                  selectionMode="single"
                  selectedKeys={selectedKeys}
                  trigger={({ ...triggerProps }) => {
                    return (
                      <PressableContainer
                        onPressIn={handlePressIn}
                        onPressOut={handlePressOut}
                        onPress={handlePress}
                        onHoverIn={() => setHoveredIndex(1)}
                        onHoverOut={() => setHoveredIndex(null)}
                        {...triggerProps}
                      >
                        <VDBIcon
                          color={
                            showViewSelected || hoveredIndex === 1
                              ? theme.isLargeScreen
                                ? theme["core-05"]
                                : theme["core-04"]
                              : getThreeDotIconBackgroundColor(
                                  theme,
                                  ProductCategory.DEFAULT
                                )
                          }
                          size={24}
                          variant={IconsVariant.VDBCustom}
                          name={VDBIcons.DOTS_MENU_FILLED}
                        />
                      </PressableContainer>
                    );
                  }}
                >
                  {menuOptions &&
                    menuOptions.map((option, index) => (
                      <MenuItem
                        key={option.name}
                        textValue={option.label}
                        style={{
                          padding: 0,
                          backgroundColor: theme.transparent,
                        }}
                        onPress={() => {
                          const keysSet = new Set([option.name as string]);
                          if (keysSet.entries.length > 0) {
                            setSelectedKeys(keysSet);
                          }
                          const [firstKey] = keysSet;
                          threeDotOptions(
                            isFavorites ? "Favorites" : collection?.name,
                            isFavorites ? count : collection?.count,
                            isFavorites ? 1 : collection?.id,
                            isFavorites,
                            firstKey
                          );
                        }}
                      >
                        <MenuItemContainer>
                          <MenuItemStyle
                            optionSize={menuOptions.length}
                            index={index}
                          >
                            <VDBText
                              color={
                                option.name === "delete_collection"
                                  ? theme["error-01"]
                                  : theme["core-05"]
                              }
                              type={typography["Minor-M"]}
                            >
                              {option.label}
                            </VDBText>
                          </MenuItemStyle>
                          {index < menuOptions.length - 1 && (
                            <LineView theme={theme} />
                          )}
                        </MenuItemContainer>
                      </MenuItem>
                    ))}
                </StyledMenu>
              )}
            </OptionsWrapper>
          </OptionsContainer>
        )}
      </ParentContainer>

      {!isFavorites && fromFavoriteScreen && (
        <CreationTimeContainer
          theme={theme}
          showSelected={showViewSelected}
          isLargeScreen={theme.isLargeScreen}
        >
          <VDBText
            color={
              theme[
                showViewSelected
                  ? theme.isLargeScreen
                    ? "core-06"
                    : "option-cont-active"
                  : "core-06"
              ]
            }
            type={typography["Minor-R"]}
          >
            {collection?.created_at === collection?.updated_at
              ? t("global.text.created")
              : t("global.text.updated")}
          </VDBText>
          <VDBText
            color={
              theme[
                showViewSelected
                  ? theme.isLargeScreen
                    ? "core-06"
                    : "option-cont-active"
                  : "core-05"
              ]
            }
            type={typography["Minor-M"]}
          >
            {collection?.updated_at ? getDate(collection?.updated_at) : ""}
          </VDBText>
        </CreationTimeContainer>
      )}

      <ShareModal
        isOpen={showShareModal}
        onClose={() => {
          setshowShareModal(false);
        }}
        collection={collection}
        favoriteIds={stock_item_ids}
        favoriteCount={count}
        shareType={isFavorites ? ShareType.favorites : ShareType.collections}
        selectedOption={selectedOption as ShareVia}
      />
      {/*)}*/}
    </TopLevelContainer>
  );
};

const TopLevelContainer = styled(View)<any>`
  margin-top: ${({ isTablet }: { isTablet: boolean }) =>
    isTablet ? SpaceType.Basic : SpaceType.Basic}px;
  margin-left: ${({ isTablet }: { isTablet: boolean }) =>
    isTablet ? SpaceType.Big : SpaceType.Basic}px;
  margin-right: ${({ isTablet }: { isTablet: boolean }) =>
    isTablet ? SpaceType.LargeDouble : SpaceType.Basic}px;
  margin-bottom: ${({ isTablet }: { isTablet: boolean }) =>
    isTablet ? 0 : SpaceType.Basic}px;
  border-radius: ${({ isLargeScreen }: { isLargeScreen: boolean }) =>
    isLargeScreen ? 10 : SpaceType.Basic}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  border-width: ${({ isLargeScreen }: { isLargeScreen: boolean }) =>
    isLargeScreen ? 2 : 1}px;
  border-color: ${({
    theme,
    isLargeScreen,
    showViewSelected,
    isFavorites,
    fromFavoriteScreen,
  }: {
    theme: DefaultTheme;
    isLargeScreen: boolean;
    showViewSelected: boolean;
    isFavorites: boolean;
    fromFavoriteScreen: boolean;
  }) =>
    isLargeScreen && (showViewSelected || (isFavorites && !fromFavoriteScreen))
      ? theme["core-05"]
      : theme["core-03"]};
  max-width: ${({ isLargeScreen }: { isLargeScreen: boolean }) =>
    isLargeScreen ? "324px" : "100%"};
`;

const ParentContainer = styled.View<any>`
  flex-direction: row;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom-left-radius: ${({
    showBorderRadius,
  }: {
    showBorderRadius: boolean;
  }) => (showBorderRadius ? 8 : 0)}px;
  border-bottom-right-radius: ${({
    showBorderRadius,
  }: {
    showBorderRadius: boolean;
  }) => (showBorderRadius ? 8 : 0)}px;
  background-color: ${({
    theme,
    showSelected,
    isLargeScreen,
    isFavorites,
    fromFavoriteScreen,
  }: {
    theme: DefaultTheme;
    showSelected: boolean;
    isLargeScreen: boolean;
    isFavorites: boolean;
    fromFavoriteScreen: boolean;
  }) =>
    theme[
      showSelected || (isFavorites && !fromFavoriteScreen)
        ? isLargeScreen
          ? "core-02"
          : "option-bg-active"
        : "core-01"
    ]};
  align-items: center;
  justify-content: space-between;
  padding: ${SpaceType.Basic}px;
`;

const IconAndCollectionContainer = styled.View`
  flex-direction: row;
  flex: 1;
`;

const IconContainer = styled.View<any>`
  flex-direction: row;
  width: 48px;
  margin-left: 2px;
  margin-right: 10px;
  align-items: center;
`;

const ImageContainer = styled.View<{
  isEmpty: boolean;
}>`
  flex-direction: row;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: ${({
    theme,
    isEmpty,
  }: {
    theme: DefaultTheme;
    isEmpty: boolean;
  }) => (!isEmpty ? theme["core-02"] : theme["core-01"])};
  align-items: center;
  justify-content: center;
  border-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-03"] as string};
`;

const CollectionInfoContainer = styled.View<any>`
  align-items: flex-start;
  flex: 1;
`;

const CountContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const OptionsContainer = styled.View<any>`
  justify-content: space-between;
  width: 40px;
  margin-left: 5px;
  height: ${({ isLargeScreen }: { isLargeScreen: boolean }) =>
    isLargeScreen ? "80px" : "auto"};
`;

const CheckboxContainer = styled.View<any>`
  justify-content: center;
  width: 24px;
  margin-left: 5px;
`;

const CreationTimeContainer = styled.View<any>`
  flex-direction: row;
  align-items: center;
  height: 32px;
  justify-content: space-between;
  background-color: ${({
    theme,
    showSelected,
    isLargeScreen,
  }: {
    theme: DefaultTheme;
    showSelected: boolean;
    isLargeScreen: boolean;
  }) =>
    theme[
      showSelected
        ? isLargeScreen
          ? "core-02"
          : "option-bg-active"
        : "core-02"
    ]};
  padding: ${SpaceType.Basic}px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const LineView = styled.View<any>`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-03"] as string};
  width: auto;
`;

const MenuItemStyle = styled.View<any>`
  border-color: #000000;
  width: 250px;
  padding: 10px;
  border-top-left-radius: ${({
    optionSize,
    index,
  }: {
    optionSize: number;
    index: number;
  }) => (optionSize === 1 || index === 0 ? 8 : 0)}px;
  border-top-right-radius: ${({
    optionSize,
    index,
  }: {
    optionSize: number;
    index: number;
  }) => (optionSize === 1 || index === 0 ? 8 : 0)}px;
  border-bottom-left-radius: ${({
    optionSize,
    index,
  }: {
    optionSize: number;
    index: number;
  }) => (index === optionSize - 1 ? 8 : 0)}px;
  border-bottom-right-radius: ${({
    optionSize,
    index,
  }: {
    optionSize: number;
    index: number;
  }) => (index === optionSize - 1 ? 8 : 0)}px;
`;

const MenuItemContainer = styled.View``;

const OptionsWrapper = styled.View<any>`
  justify-content: ${({ menuOptionsSize }: { menuOptionsSize: number }) =>
    menuOptionsSize > 0 ? `space-between` : `center`};
`;

const PressableContainer = styled.Pressable`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledMenu = styled(Menu)`
  box-shadow: ${({ theme }: { theme: DefaultTheme }) =>
    getBoxShadow(theme.Shadows.Huge)};
`;
export default CollectionView;
